import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { BerService } from '../ber.service';

/**
 *  ELECTRA .HUF CSOMAG – banki átutalási állomány (backend/ber-electra.js).
 *
 *  Kétféle csomagot ad: a havi NETTÓ BÉREKET (`fajta='ber'`) és a LETILTÁSOK kedvezményezetti
 *  utalásait (`fajta='letiltas'`). Letöltés előtt ELŐNÉZETET kér a kiszolgálótól: mi kerül a
 *  fájlba, mi marad ki és miért (hiányzó bankszámlaszám), és megmutatja az első rekordot is –
 *  a kötött formátumnál ez az egyetlen módja, hogy a fájl tartalma ellenőrizhető legyen.
 */
@Component({
  selector: 'app-ber-electra-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './electra-panel.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss'],
  styles: [`
    :host { padding-bottom: 0; }
    .el-fej { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .el-osszeg { font-variant-numeric: tabular-nums; b { font-size: 1.05rem; } }
    .el-beall { display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-end; margin: 10px 0 4px; }
    .el-beall label { font-size: .74rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--n-muted); display: block; }
    .el-beall .mezo { background: #f1f3f6; border-radius: 6px; padding: 3px 8px; min-width: 120px; }
    .el-beall input, .el-beall select { border: 0; background: transparent; font: inherit; padding: 2px 0; width: 100%; }
    .el-minta { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .68rem; line-height: 1.5;
      background: #0f172a; color: #e2e8f0; border-radius: 6px; padding: 10px; overflow-wrap: anywhere; }
    .el-fig { color: #b45309; font-size: .82rem; }
    .el-kimaradt { color: var(--n-neg); font-size: .84rem; }
    .cimke.piros { background: #fdecec; color: #b42318; }
  `],
})
export class BerElectraPanelComponent implements OnInit, OnChanges, OnDestroy {
  /** 'ber' = havi nettó bérek, 'letiltas' = letiltások kedvezményezetti utalásai. */
  @Input() fajta: 'ber' | 'letiltas' = 'ber';
  /*  Az év/hónap alapból a BÉR-modul aktív hónapja, de van oldal (Letiltások), ahol saját
   *  szűrője van – ott azt kapjuk meg, különben más hónapra készülne a csomag, mint amit a
   *  felhasználó a képernyőn lát. */
  @Input() ev?: number;
  @Input() ho?: number;

  nyitva = false;
  tolt = false;
  elonezet: any = null;
  hibaSzoveg = '';
  mintaLatszik = false;
  beallLatszik = false;
  beall = { nap: '', kodlap: 'latin2', ekezet: true, szamlaigazitas: 'bal', megbizas: '', tipus: '' };

  private sub?: Subscription;
  constructor(private ber: BerService, private toast: ToastController) {}

  ngOnInit() {
    // Év/hónap vagy ügyfélváltásnál a nyitott előnézet magától frissül.
    this.sub = this.ber.aktiv$.subscribe(() => { if (this.nyitva) this.betolt(); });
  }
  ngOnChanges(v: SimpleChanges) {
    if ((v['ev'] || v['ho']) && this.nyitva) this.betolt();
  }
  ngOnDestroy() { this.sub?.unsubscribe(); }

  private get evho() {
    const a = this.ber.aktiv;
    return { ev: Number(this.ev) || a.ev, ho: Number(this.ho) || a.ho };
  }

  get cim() { return this.fajta === 'ber' ? 'Electra átutalási csomag (nettó bérek)' : 'Electra átutalási csomag (letiltások)'; }

  async valt() {
    this.nyitva = !this.nyitva;
    if (this.nyitva && !this.elonezet) await this.betolt();
  }

  async betolt() {
    const a = this.ber.aktiv;
    if (!a.ugyfelId) return;
    this.tolt = true; this.hibaSzoveg = '';
    try {
      this.elonezet = await this.ber.electraApi.elonezet(this.ut + this.qs(true));
    } catch (e: any) {
      this.elonezet = null;
      this.hibaSzoveg = e?.error?.error || 'Nem sikerült elkészíteni az előnézetet.';
    }
    this.tolt = false;
  }

  async letolt() {
    if (!this.elonezet?.db) return;
    await this.ber.electraApi.letoltes(this.ut + this.qs(false));
    await this.uzenet(`${this.elonezet.fajlnev} – ${this.elonezet.db} átutalás, ${Number(this.elonezet.osszeg).toLocaleString('hu-HU')} Ft.`);
  }

  private get ut() { return `/${this.fajta}/${this.ber.aktiv.ugyfelId}`; }

  private qs(elonezet: boolean): string {
    const { ev, ho } = this.evho;
    const p = new URLSearchParams();
    p.set('ev', String(ev)); p.set('ho', String(ho));
    if (this.beall.nap) p.set('nap', this.beall.nap);
    if (this.beall.kodlap !== 'latin2') p.set('kodlap', this.beall.kodlap);
    if (!this.beall.ekezet) p.set('ekezet', 'nincs');
    if (this.beall.szamlaigazitas === 'jobb') p.set('szamlaigazitas', 'jobb');
    if (this.beall.megbizas) p.set('megbizas', this.beall.megbizas);
    if (this.fajta === 'ber' && this.beall.tipus) p.set('tipus', this.beall.tipus);
    if (elonezet) p.set('elonezet', '1');
    return '?' + p.toString();
  }

  async valtozott() { if (this.nyitva) await this.betolt(); }

  private async uzenet(m: string, szin = 'success') {
    (await this.toast.create({ message: m, duration: 2400, color: szin })).present();
  }
}
